import type { QRCode as QrCodeModel } from 'qrcode'

/**
 * QR code for the project's contact card (PLAN-084). The QR carries the
 * vCard *text* itself, not a link: a phone camera then offers "add contact"
 * without a network round-trip or a login, and the card is ~300 bytes —
 * far below where a direct payload stops scanning comfortably (~1 kB).
 *
 * `qrcode` only computes the module matrix here; the SVG is ours, so the
 * component renders a plain React element and the test can rasterise the
 * very same matrix and decode it.
 */

export const CONTACT_QR_OPTIONS = {
  /**
   * H (~30 % of codewords recoverable): the HUB wordmark sits on a plate in
   * the middle of the symbol, and the plate must stay far inside what the
   * error correction absorbs.
   */
  errorCorrectionLevel: 'H',
  /** Modules of quiet zone around the symbol (spec minimum is 4). */
  margin: 4,
  /** Width of the logo plate as a share of the symbol (≤ 20 %: ~4 % of the area). */
  logoShare: 0.2,
} as const

export interface QrMatrix {
  /** Modules per side, excluding the quiet zone. */
  size: number
  /** Row-major, `true` = dark module. */
  dark: boolean[]
}

/** The module matrix for `text` — loaded on demand so the encoder stays out of the initial bundle. */
export async function contactQrMatrix(text: string): Promise<QrMatrix> {
  const { create } = await import('qrcode')
  return matrixFromModel(
    create(text, { errorCorrectionLevel: CONTACT_QR_OPTIONS.errorCorrectionLevel })
  )
}

export function matrixFromModel(model: QrCodeModel): QrMatrix {
  const { size, data } = model.modules
  return { size, dark: Array.from(data, (bit) => bit === 1) }
}

/**
 * One SVG `path` covering every dark module, in module units; the viewBox
 * includes the quiet zone. Always black on white — scanners want contrast,
 * not the app's theme.
 */
export function qrSvgPath(matrix: QrMatrix): string {
  const m = CONTACT_QR_OPTIONS.margin
  const parts: string[] = []
  for (let row = 0; row < matrix.size; row++) {
    let run = 0
    for (let col = 0; col <= matrix.size; col++) {
      const dark = col < matrix.size && matrix.dark[row * matrix.size + col]
      if (dark) {
        run++
        continue
      }
      if (run > 0) parts.push(`M${col - run + m} ${row + m}h${run}v1h-${run}z`)
      run = 0
    }
  }
  return parts.join('')
}

export function qrViewBoxSize(matrix: QrMatrix): number {
  return matrix.size + CONTACT_QR_OPTIONS.margin * 2
}

export interface QrLogoPlate {
  /** Top-left corner and edge, in module units of the viewBox (quiet zone included). */
  x: number
  y: number
  size: number
}

/**
 * Where the logo plate goes: a square of `logoShare` × the symbol width,
 * centred, never wider than that share. Whole modules, and the same parity
 * as the symbol so the centre is the centre — an odd symbol gets an odd plate.
 */
export function qrLogoPlate(matrix: QrMatrix): QrLogoPlate {
  let size = Math.floor(matrix.size * CONTACT_QR_OPTIONS.logoShare)
  if (size % 2 !== matrix.size % 2) size -= 1
  const offset = (matrix.size - size) / 2 + CONTACT_QR_OPTIONS.margin
  return { x: offset, y: offset, size }
}

/**
 * Pixels the way a camera would see the symbol: grayscale RGBA at `scale`
 * pixels per module, quiet zone included. Used by the decode test.
 */
export function rasterizeQr(
  matrix: QrMatrix,
  scale = 4,
  options: { logoPlate?: boolean } = {}
): { data: Uint8ClampedArray; width: number; height: number } {
  const side = qrViewBoxSize(matrix) * scale
  const data = new Uint8ClampedArray(side * side * 4).fill(255)
  const m = CONTACT_QR_OPTIONS.margin
  const plate = options.logoPlate ? qrLogoPlate(matrix) : null
  for (let row = 0; row < matrix.size; row++) {
    for (let col = 0; col < matrix.size; col++) {
      if (!matrix.dark[row * matrix.size + col]) continue
      // The plate hides every module under it — worst case for the decoder.
      if (
        plate &&
        col + m >= plate.x &&
        col + m < plate.x + plate.size &&
        row + m >= plate.y &&
        row + m < plate.y + plate.size
      ) {
        continue
      }
      for (let y = 0; y < scale; y++) {
        for (let x = 0; x < scale; x++) {
          const px = ((row + m) * scale + y) * side + (col + m) * scale + x
          data[px * 4] = data[px * 4 + 1] = data[px * 4 + 2] = 0
        }
      }
    }
  }
  return { data, width: side, height: side }
}
